import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Descriptions,
  Tag,
  Space,
  Typography,
  Spin,
  Button,
  message,
} from 'antd';
import { EditOutlined, DeleteOutlined, RobotOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { mekakApi, formatDateShort, tagColor } from '../services';
import type { Knowledge, KnowledgeImage } from '../models';
import ImageGallery from './ImageGallery';

const { Text, Paragraph } = Typography;

interface KnowledgeDetailModalProps {
  open: boolean;
  knowledgeId: string | null;
  onClose: () => void;
  onDeleted?: () => void;
}

const KnowledgeDetailModal: React.FC<KnowledgeDetailModalProps> = ({
  open,
  knowledgeId,
  onClose,
  onDeleted,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [knowledge, setKnowledge] = useState<Knowledge | null>(null);
  const [images, setImages] = useState<KnowledgeImage[]>([]);

  const fetchData = useCallback(async () => {
    if (!knowledgeId) return;
    setLoading(true);
    try {
      const res = await mekakApi.getKnowledge(knowledgeId);
      if (res.data.success) {
        const k = res.data.data;
        setKnowledge(k);
        // Fetch images (works for any knowledge entry)
        try {
          const imgRes = await mekakApi.getKnowledgeImages(knowledgeId);
          if (imgRes.data.success) setImages(imgRes.data.data ?? []);
        } catch { /* no images */ }
      }
    } catch {
      message.error('載入知識條目失敗');
    } finally {
      setLoading(false);
    }
  }, [knowledgeId]);

  useEffect(() => {
    if (open && knowledgeId) {
      setKnowledge(null);
      setImages([]);
      fetchData();
    }
  }, [open, knowledgeId, fetchData]);

  const handleDelete = () => {
    if (!knowledgeId) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除知識「${knowledge?.title}」嗎？此操作無法復原。`,
      okText: '刪除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        await mekakApi.deleteKnowledge(knowledgeId);
        message.success('知識刪除成功');
        onClose();
        onDeleted?.();
      },
    });
  };

  const handleEdit = () => {
    onClose();
    navigate(`/mekak/knowledge/${knowledgeId}/edit`);
  };

  return (
    <Modal
      title={knowledge?.title ?? '知識詳情'}
      open={open}
      onCancel={onClose}
      width={800}
      footer={
        knowledge
          ? [
              <Button key="edit" icon={<EditOutlined />} onClick={handleEdit}>
                編輯
              </Button>,
              <Button key="delete" danger icon={<DeleteOutlined />} onClick={handleDelete}>
                刪除
              </Button>,
              <Button key="close" type="primary" onClick={onClose}>
                關閉
              </Button>,
            ]
          : null
      }
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : knowledge ? (
        <div style={{ maxHeight: 'calc(80vh - 120px)', overflow: 'auto' }}>
          {/* Basic info */}
          <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="作者">{knowledge.author || '-'}</Descriptions.Item>
            <Descriptions.Item label="瀏覽次數">{knowledge.view_count ?? 0}</Descriptions.Item>
            <Descriptions.Item label="建立日期">{formatDateShort(knowledge.created_at)}</Descriptions.Item>
            <Descriptions.Item label="更新日期">{formatDateShort(knowledge.updated_at)}</Descriptions.Item>
            <Descriptions.Item label="標籤" span={2}>
              {knowledge.tags?.length ? (
                <Space size={4} wrap>
                  {knowledge.tags.map(t => (
                    <Tag key={t} color={tagColor(t)}>{t}</Tag>
                  ))}
                </Space>
              ) : (
                <Text type="secondary">-</Text>
              )}
            </Descriptions.Item>
          </Descriptions>

          {/* Content */}
          <div
            style={{
              background: '#fafafa',
              borderRadius: 6,
              padding: 16,
              marginBottom: 16,
              whiteSpace: 'pre-wrap',
              lineHeight: 1.8,
              fontSize: 14,
              maxHeight: 300,
              overflow: 'auto',
            }}
          >
            {knowledge.content}
          </div>

          {/* Images */}
          {images.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                相關圖片 ({images.length})
              </Text>
              <ImageGallery images={images} />
            </div>
          )}

          {/* AI Metadata */}
          {knowledge.metadata?.preprocessed && (
            <Descriptions
              bordered
              size="small"
              column={2}
              title={
                <Space>
                  <RobotOutlined />
                  <span>AI 預處理資訊</span>
                </Space>
              }
            >
              <Descriptions.Item label="主題">{knowledge.metadata.main_topic || '-'}</Descriptions.Item>
              <Descriptions.Item label="分段數">{knowledge.metadata.chunk_count ?? 0}</Descriptions.Item>
              <Descriptions.Item label="LLM Tokens">{(knowledge.llm_tokens ?? 0).toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="成本">
                {knowledge.total_cost ? `$${knowledge.total_cost.toFixed(6)}` : '-'}
              </Descriptions.Item>
            </Descriptions>
          )}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Text type="secondary">找不到此知識條目</Text>
        </div>
      )}
    </Modal>
  );
};

export default KnowledgeDetailModal;
